import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSiteData } from '../context/SiteDataContext';
import { DAY_KEYS } from '../lib/types';

const TITLES: Record<string, string> = {
  '/': 'Apex Car Care | Premium Auto Detailing in Ottawa',
  '/services': 'Detailing Packages & Pricing | Apex Car Care Ottawa',
  '/custom': 'Custom Audio, Lighting & Wraps | Apex Car Care Ottawa',
  '/gallery': 'Our Work — Before & After Gallery | Apex Car Care',
  '/booking': 'Book Your Detail Online | Apex Car Care Ottawa',
  '/contact': 'Contact Us | Apex Car Care Ottawa',
  '/login': 'Sign In | Apex Car Care',
  '/account': 'My Account | Apex Car Care',
};

const DAY_SCHEMA: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

/** Per-page titles + LocalBusiness structured data for Google. */
export default function Seo() {
  const { pathname } = useLocation();
  const { settings, testimonials } = useSiteData();

  useEffect(() => {
    document.title = TITLES[pathname] ?? TITLES['/'];
  }, [pathname]);

  useEffect(() => {
    const schema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'AutoRepair',
      additionalType: 'https://schema.org/AutoDetailing',
      name: settings.businessName,
      description: settings.heroSubtitle,
      url: 'https://apexcarcare.net',
      telephone: settings.phone,
      email: settings.email,
      priceRange: '$$',
      address: { '@type': 'PostalAddress', addressLocality: settings.city, addressRegion: 'ON', addressCountry: 'CA' },
      areaServed: settings.serviceArea,
      openingHoursSpecification: DAY_KEYS.filter((d) => !settings.hours[d]?.closed).map((d) => ({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: DAY_SCHEMA[d],
        opens: settings.hours[d].open,
        closes: settings.hours[d].close,
      })),
    };
    if (testimonials.length > 0) {
      const avg = testimonials.reduce((s, t) => s + t.rating, 0) / testimonials.length;
      schema.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: avg.toFixed(1),
        reviewCount: testimonials.length,
      };
    }
    let el = document.getElementById('ld-json') as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = 'ld-json';
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(schema);
  }, [settings, testimonials]);

  return null;
}
